import { MatchMvp } from '@/components/MatchMvp';
import { appUrl } from '@/lib/appUrl';
import { formatSeriesDate } from '@/lib/matchTime';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { useAuth } from '@/contexts/AuthContext';
import { matchesAPI, api } from '@/lib/api';
import GoalkeeperRotationPanel from '@/components/GoalkeeperRotationPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Calendar, Users, MapPin, Clock, ExternalLink,
  Settings, UserPlus, UserMinus, Trophy, Share2,
  Goal, Bell, Send, MessageCircle, LayoutGrid, Trash2, Building2, Euro, Shuffle
} from 'lucide-react';
import {
  getInitials, formatDate, formatTime,
  getStatusColorClass, getStatusLabel, isPast 
} from '@/lib/utils';
import { MatchEventsSection, MatchMediaGallery } from '@/components/MatchEventsManager';
import { PlayerProfileDialog } from '@/components/PlayerProfileDialog';
import { FifaMiniCard } from '@/components/FifaCard';
import { MatchPaymentsSection } from '@/components/MatchPaymentsSection';

// =============================================================================
// Mini-components for the rich join button (Match C)
// =============================================================================

const fireConfetti = () => {
  const end = Date.now() + 600;
  const colors = ['#10b981', '#3b82f6', '#f59e0b'];
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60, spread: 55, startVelocity: 45,
      origin: { x: 0, y: 0.7 }, colors,
    });
    confetti({
      particleCount: 4,
      angle: 120, spread: 55, startVelocity: 45,
      origin: { x: 1, y: 0.7 }, colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
};

const Countdown = ({ dateTime }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!dateTime) return null;
  const diff = new Date(dateTime).getTime() - now;
  if (diff <= 0) return null;

  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  let label;
  if (days >= 1) label = `Tra ${days}g ${hours % 24}h`;
  else if (hours >= 1) label = `Tra ${hours}h ${minutes % 60}m`;
  else label = `Tra ${minutes}m`;

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold shadow-sm">
      <Clock className="h-3 w-3" />
      {label}
    </div>
  );
};

const ParticipantsAvatarStack = ({ participants, max = 4 }) => {
  if (!participants?.length) return null;
  const visible = participants.slice(0, max);
  const extra = Math.max(0, participants.length - max);
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2">
        {visible.map((p) => {
          const u = p.user || {};
          return (
            <div
              key={p.user_id}
              className="w-7 h-7 rounded-full border-2 border-background overflow-hidden bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs font-bold flex items-center justify-center"
              title={u.name}
            >
              {u.picture
                ? <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                : (u.name || '?').slice(0, 2).toUpperCase()}
            </div>
          );
        })}
        {extra > 0 && (
          <div className="w-7 h-7 rounded-full border-2 border-background bg-muted text-foreground text-xs font-bold flex items-center justify-center">
            +{extra}
          </div>
        )}
      </div>
      <span className="text-xs text-muted-foreground">
        {visible.map((p) => p.user?.nickname || p.user?.name || '').filter(Boolean).slice(0, 2).join(', ')}
        {extra > 0 ? ` e altri ${extra}` : ''}
      </span>
    </div>
  );
};

const RichJoinButton = ({
  match, userParticipation, confirmedParticipants, reserveParticipants,
  isFull, reservesFull, onJoin, onLeave, currentUserId,
}) => {
  const cancelled = match.status === 'cancelled';

  if (userParticipation?.status === 'confirmed') {
    return (
      <Button variant="outline" onClick={onLeave}>
        <UserMinus className="h-4 w-4 mr-2" />
        Lascia
      </Button>
    );
  }
  if (userParticipation?.status === 'reserve') {
    const pos = reserveParticipants.findIndex((p) => p.user_id === currentUserId) + 1;
    return (
      <Button variant="outline" onClick={onLeave}>
        <UserMinus className="h-4 w-4 mr-2" />
        Lascia (Riserva #{pos})
      </Button>
    );
  }
  if (userParticipation?.status === 'requested') {
    return <Button variant="outline" disabled>In attesa</Button>;
  }

  if (cancelled) {
    return <Button disabled>Partita annullata</Button>;
  }

  const maxTitolari = match.max_participants || 10;
  const titolariLeft = maxTitolari - confirmedParticipants.length;
  const reservesPos = reserveParticipants.length + 1;

  // Both full
  if (isFull && reservesFull) {
    return (
      <Button disabled className="bg-rose-100 text-rose-700 hover:bg-rose-100 cursor-not-allowed dark:bg-rose-950/40 dark:text-rose-300 dark:hover:bg-rose-950/40">
        🚫 Lista chiusa
      </Button>
    );
  }

  // Titolari pieni → reserve
  if (isFull) {
    return (
      <Button
        onClick={onJoin}
        className="bg-slate-700 hover:bg-slate-800 text-white"
        data-testid="join-match-btn"
      >
        🪑 Iscriviti riserva (#{reservesPos})
      </Button>
    );
  }

  // Last spot
  if (titolariLeft === 1) {
    return (
      <Button
        onClick={onJoin}
        className="bg-amber-500 hover:bg-amber-600 text-white animate-pulse"
        data-testid="join-match-btn"
      >
        ⚡ Ultimo posto disponibile!
      </Button>
    );
  }

  // Many spots free
  return (
    <Button
      onClick={onJoin}
      className="bg-emerald-500 hover:bg-emerald-600 text-white"
      data-testid="join-match-btn"
    >
      <UserPlus className="h-4 w-4 mr-2" />
      Iscriviti come Titolare ({titolariLeft} posti)
    </Button>
  );
};

// '2026-10-15T20:00' → 'gio 15/10 alle 20:00'
// Quick jumps through the long match page on phones; closed sections open on arrival.
const MATCH_SECTIONS = [['mvp', 'MVP'], ['info', 'Info'], ['campo', 'Campo'], ['squadre', 'Squadre'], ['riserve', 'Riserve'], ['eventi', 'Eventi']];
const jumpToSection = (sectionId) => {
  const el = document.getElementById(sectionId);
  if (!el) return;
  if (el.tagName === 'DETAILS') el.open = true;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const MatchDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [mvp, setMvp] = useState(null);
  const [series, setSeries] = useState(null);
  const [league, setLeague] = useState(null);
  const [sport, setSport] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [teamAInfo, setTeamAInfo] = useState(null);
  const [teamBInfo, setTeamBInfo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [userParticipation, setUserParticipation] = useState(null);
  const [userMembership, setUserMembership] = useState(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [joining, setJoining] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  
  // Result dialog state
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [scoreA, setScoreA] = useState('');
  const [scoreB, setScoreB] = useState('');
  const [savingResult, setSavingResult] = useState(false);
  
  // Notification dialog state
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);

  // Roster management state
  const [rosterCandidates, setRosterCandidates] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [selectedRosterUser, setSelectedRosterUser] = useState('');
  const [selectedRosterTeam, setSelectedRosterTeam] = useState('A');
  const [selectedRosterStatus, setSelectedRosterStatus] = useState('confirmed');
  const [rosterSearch, setRosterSearch] = useState('');
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [draggingUserId, setDraggingUserId] = useState(null);

  const isArchived = !!match?.archived_season;
  const isOrganizer = !isArchived && (
    match?.created_by === user?.user_id ||
    user?.role === 'admin' ||
    ['admin', 'manager'].includes(userMembership?.role)
  );
  const canManageRoster = isOrganizer;

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (match && canManageRoster) {
      fetchRosterCandidates(match.match_id);
    }
  }, [match, canManageRoster]);

  // Live refresh: poll every 8s while the tab is visible, plus refetch on
  // focus / visibility-return. Keeps the participants list current without a
  // page reload — important on the "primo arriva primo gioca" flow on PWA.
  useEffect(() => {
    if (!id) return;
    let intervalId = null;

    const start = () => {
      if (intervalId == null) {
        intervalId = setInterval(() => fetchData({ silent: true }), 8000);
      }
    };
    const stop = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true });
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => fetchData({ silent: true });

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [id]);

  const handleStopSeries = async () => {
    try {
      await matchesAPI.stopSeries(series.series_id);
      setSeries(null);
      toast.success('Ripetizione interrotta: le partite già uscite restano');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const fetchData = async ({ silent = false } = {}) => {
    try {
      const res = await matchesAPI.getById(id);
      setMatch(res.data.match);
      setMvp(res.data.mvp);
      setSeries(res.data.series || null);
      setLeague(res.data.league);
      setSport(res.data.sport);
      setCompetition(res.data.competition);
      setTeamAInfo(res.data.team_a);
      setTeamBInfo(res.data.team_b);
      setParticipants(res.data.participants);
      setUserParticipation(res.data.user_participation);
      setUserMembership(res.data.user_membership || null);
    } catch (error) {
      console.error('Error fetching match:', error);
      // Don't spam toast on background polls — only the initial/manual load
      if (!silent) toast.error(error.response?.status === 403 ? 'Partita privata: la vedono solo i membri' : 'Errore nel caricamento della partita');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (mvp?.has_started && location.hash === '#mvp') {
      document.getElementById('mvp')?.scrollIntoView({ block: 'start' });
    }
  }, [mvp?.has_started, id, location.hash]);

  const fetchRosterCandidates = async (matchId) => {
    setLoadingRoster(true);
    try {
      const res = await matchesAPI.getRosterCandidates(matchId);
      setRosterCandidates(res.data.users || []);
    } catch (error) {
      console.error('Error fetching roster candidates:', error);
    } finally {
      setLoadingRoster(false);
    }
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      await matchesAPI.join(id, {});
      toast.success('🎉 Iscrizione completata!');
      fireConfetti();
      setShowJoinDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'iscrizione');
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Sei sicuro di voler lasciare questa partita?')) return;
    
    try {
      await matchesAPI.leave(id);
      toast.success('Hai lasciato la partita');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'uscita dalla partita');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await matchesAPI.update(id, { status: newStatus });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // Save match result
  const handleSaveResult = async () => {
    if (scoreA === '' || scoreB === '') {
      toast.error('Inserisci entrambi i punteggi');
      return;
    }
    
    setSavingResult(true);
    try {
      await matchesAPI.update(id, { 
        result: {
          team_a_score: parseInt(scoreA),
          team_b_score: parseInt(scoreB)
        },
        status: 'completed'
      });
      toast.success('Risultato salvato!');
      setShowResultDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio del risultato');
    } finally {
      setSavingResult(false);
    }
  };

  // Announce the match to every member of the league
  const handleAnnounceMatch = async () => {
    setSendingNotifications(true);
    try {
      const res = await api.post(`/notify/match-reminder/${id}`);
      toast.success(res.data.message || 'Notifica inviata alla lega!');
      setShowNotifyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle notifiche');
    } finally {
      setSendingNotifications(false);
    }
  };

  // Remind only the players who already confirmed
  const handleRemindParticipants = async () => {
    setSendingNotifications(true);
    try {
      const res = await api.post(`/notify/match-participants/${id}`);
      toast.success(res.data.message || 'Promemoria inviato!');
      setShowNotifyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del promemoria');
    } finally {
      setSendingNotifications(false);
    }
  };

  // Share match on WhatsApp
  const handleShareWhatsApp = () => {
    const text = `🏆 *${match.title}*\n\n📅 ${formatDate(match.date_time)} alle ${formatTime(match.date_time)}\n📍 ${match.location_name}\n\nPartecipa! ${appUrl(`/matches/${id}`)}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleOpenAddPlayerDialog = () => {
    setRosterSearch('');
    setSelectedRosterUser('');
    setShowAddPlayerDialog(true);
  };

  const handleAddParticipant = async (guestName = '') => {
    const normalizedGuestName = guestName.trim();
    if (!normalizedGuestName && !selectedRosterUser) {
      toast.error('Seleziona un giocatore');
      return;
    }
    setAddingParticipant(true);
    try {
      await matchesAPI.addParticipant(id, {
        ...(normalizedGuestName ? { name: normalizedGuestName } : { user_id: selectedRosterUser }),
        team: selectedRosterTeam === 'none' ? null : selectedRosterTeam,
        status: selectedRosterStatus
      });
      toast.success(normalizedGuestName ? `${normalizedGuestName} aggiunto come ospite` : 'Giocatore aggiunto alla rosa');
      setSelectedRosterUser('');
      setRosterSearch('');
      setShowAddPlayerDialog(false);
      fetchData();
      fetchRosterCandidates(id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta giocatore');
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleUpdateParticipant = async (userId, data) => {
    try {
      await matchesAPI.updateParticipant(id, userId, data);
      fetchData();
    } catch (error) {
      toast.error('Errore aggiornamento giocatore');
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!window.confirm('Rimuovere il giocatore dalla rosa?')) return;
    try {
      await matchesAPI.removeParticipant(id, userId);
      fetchData();
      fetchRosterCandidates(id);
    } catch (error) {
      toast.error('Errore rimozione giocatore');
    }
  };

  // Drag-and-drop / quick-move for roster board
  const handleMoveToZone = async (userId, zone) => {
    const participant = participants.find(p => p.user_id === userId);
    if (!participant) return;

    const targetStatus = zone === 'reserve' ? 'reserve' : 'confirmed';
    const targetTeam = zone === 'A' ? 'A' : zone === 'B' ? 'B' : null;

    if (participant.status === targetStatus && participant.team === targetTeam) return;

    if (targetStatus === 'confirmed' && participant.status !== 'confirmed' && confirmedParticipants.length >= (match?.max_participants || 10)) {
      toast.error('Titolari pieni');
      return;
    }
    if (targetStatus === 'reserve' && participant.status !== 'reserve' && reserveParticipants.length >= maxReserves) {
      toast.error('Riserve piene');
      return;
    }
    if (targetTeam && isTeamLocked && participant.team !== targetTeam) {
      toast.error('Le squadre sono fissate');
      return;
    }

    const payload = { status: targetStatus, team: targetStatus === 'reserve' ? null : targetTeam };
    try {
      await matchesAPI.updateParticipant(id, userId, payload);
      fetchData();
    } catch (error) {
      toast.error('Errore aggiornamento giocatore');
    }
  };

  // ===== Team-builder =====
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);

  const handleSuggestTeams = async () => {
    setSuggestLoading(true);
    setSuggestion(null);
    setShowSuggestDialog(true);
    try {
      const res = await matchesAPI.suggestTeams(id);
      setSuggestion(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione squadre');
      setShowSuggestDialog(false);
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleApplySuggestion = async () => {
    if (!suggestion) return;
    setApplyingSuggestion(true);
    try {
      const assignments = [
        ...suggestion.team_a.map((p) => ({ user_id: p.user_id, team: 'A' })),
        ...suggestion.team_b.map((p) => ({ user_id: p.user_id, team: 'B' })),
      ];
      for (const a of assignments) {
        // eslint-disable-next-line no-await-in-loop
        await matchesAPI.updateParticipant(id, a.user_id, { status: 'confirmed', team: a.team });
      }
      toast.success('Squadre applicate, premi "Conferma squadre" per avvisare i giocatori');
      setShowSuggestDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore applicazione squadre');
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const handleNotifyTeams = async () => {
    try {
      const res = await matchesAPI.notifyTeams(id);
      toast.success(`Squadre confermate, ${res.data.notified} giocatori avvisati`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio notifica squadre');
    }
  };

  const confirmedParticipants = participants.filter(p => p.status === 'confirmed');
  const reserveParticipants = participants.filter(p => p.status === 'reserve');
  const teamA = confirmedParticipants.filter(p => p.team === 'A');
  const teamB = confirmedParticipants.filter(p => p.team === 'B');
  const noTeam = confirmedParticipants.filter(p => !p.team);
  const getTeamSkillTotal = (team) => team.reduce(
    (total, participant) => total + (participant.user?.skill_level ?? 3),
    0,
  );
  const teamASkillTotal = getTeamSkillTotal(teamA);
  const teamBSkillTotal = getTeamSkillTotal(teamB);
  
  const isFull = confirmedParticipants.length >= (match?.max_participants || 10);
  const maxReserves = match?.max_reserves || 5;
  const reservesFull = reserveParticipants.length >= maxReserves;
  const isTeamLocked = !!(match?.team_a_id || match?.team_b_id);

  const availableRosterUsers = rosterCandidates.filter(u => !participants.some(p => p.user_id === u.user_id));
  const normalizedRosterSearch = rosterSearch.trim().toLowerCase();
  const filteredRosterUsers = normalizedRosterSearch.length
    ? availableRosterUsers.filter(u => {
        const name = (u.name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(normalizedRosterSearch) || email.includes(normalizedRosterSearch);
      })
    : availableRosterUsers;
  const rosterPlaceholder = loadingRoster
    ? 'Caricamento...'
    : (availableRosterUsers.length ? 'Seleziona giocatore' : 'Nessun membro disponibile');

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="p-4 md:p-6 lg:p-8 text-center">
        <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Partita non trovata</h2>
        <Link to="/matches">
          <Button>Torna alle partite</Button>
        </Link>
      </div>
    );
  }

  // ─── Hero gradient picker by status ────────────────────────────────────────
  const heroGradient = match.status === 'completed'
    ? 'from-slate-700 to-slate-900'
    : match.status === 'cancelled'
      ? 'from-rose-600 to-pink-700'
      : match.status === 'ongoing'
        ? 'from-blue-600 to-indigo-700'
        : 'from-emerald-500 to-blue-600';

  return (
    <div data-testid="match-detail">
      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <div className={`bg-gradient-to-br ${heroGradient} text-white`}>
        <div className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="space-y-2 min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                  🏆 {sport?.name}
                </Badge>
                <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm">
                  {mvp?.has_started ? 'Terminata' : getStatusLabel(match.status)}
                </Badge>

              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold drop-shadow-lg">{match.title}</h1>
              {league?.name && (
                <Link to={`/leagues/${league?.league_id}`} className="text-white/90 hover:text-white transition-colors flex items-center gap-1 text-sm">
                  <Trophy className="h-3.5 w-3.5" />
                  {league.name}
                </Link>
              )}
              {competition && (
                <div className="text-xs text-white/80 flex items-center gap-1">
                  <Trophy className="h-3.5 w-3.5" />
                  {competition.name} · {competition.competition_type === 'tournament' ? 'Torneo' : 'Campionato'}
                </div>
              )}
              {isArchived && (
                <Badge className="mt-2 bg-white/15 text-white border-white/30 hover:bg-white/15">
                  Partita archiviata
                </Badge>
              )}

              {/* SCORE in hero — visibilissimo */}
              {match.status === 'completed' && match.result && (
                <div className="mt-4 flex items-center gap-5 sm:gap-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 max-w-md">
                  <div className="text-center flex-1">
                    <div className="text-xs uppercase tracking-wider opacity-80">Team A</div>
                    <div className="text-4xl sm:text-5xl font-bold tabular-nums">{match.result.team_a_score}</div>
                    {teamA.length > 0 && (
                      <div className="text-xs mt-1 opacity-90">{teamASkillTotal} pt skill</div>
                    )}
                  </div>
                  <div className="text-2xl opacity-60">·</div>
                  <div className="text-center flex-1">
                    <div className="text-xs uppercase tracking-wider opacity-80">Team B</div>
                    <div className="text-4xl sm:text-5xl font-bold tabular-nums">{match.result.team_b_score}</div>
                    {teamB.length > 0 && (
                      <div className="text-xs mt-1 opacity-90">{teamBSkillTotal} pt skill</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 items-start lg:items-end">
              {isOrganizer && !mvp?.has_started && match.status !== 'completed' && match.status !== 'cancelled' && (
                <Button
                  onClick={() => navigate(`/matches/${id}/live`)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg font-semibold"
                  data-testid="match-live-button"
                >
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/80"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                  </span>
                  Modalità Live
                </Button>
              )}
              {isOrganizer && (
                <details className="rounded-lg border border-white/30 bg-white/10 p-2">
                  <summary className="cursor-pointer px-2 py-1 text-sm font-medium">Gestisci partita</summary>
                  <div className="flex flex-wrap gap-2 mt-3 max-w-sm">
                  <Select value={match.status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-[140px] bg-white/15 border-white/30 text-white backdrop-blur-sm hover:bg-white/25" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Bozza</SelectItem>
                      <SelectItem value="confirmed">Confermata</SelectItem>
                      <SelectItem value="cancelled">Annullata</SelectItem>
                      <SelectItem value="completed">Completata</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/matches/${id}/edit`)}
                    className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    <span>Modifica</span>
                  </Button>
                  {match.status !== 'completed' && (
                    <Button
                      variant="outline"
                      onClick={handleSuggestTeams}
                      className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                      data-testid="suggest-teams-btn"
                    >
                      <Shuffle className="h-4 w-4 mr-2" />
                      <span>Genera squadre</span>
                    </Button>
                  )}
                  {match.status !== 'completed' && (teamA.length > 0 || teamB.length > 0) && (
                    <Button
                      variant="outline"
                      onClick={handleNotifyTeams}
                      className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                      data-testid="confirm-teams-btn"
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      <span>Conferma squadre</span>
                    </Button>
                  )}
                  {match.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (match.result) {
                          setScoreA(match.result.team_a_score?.toString() || '');
                          setScoreB(match.result.team_b_score?.toString() || '');
                        }
                        setShowResultDialog(true);
                      }}
                      data-testid="insert-result-btn"
                      className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                    >
                      <Goal className="h-4 w-4 mr-2" />
                      <span>Risultato</span>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setShowNotifyDialog(true)}
                    data-testid="notify-btn"
                    className="bg-white/15 border-white/30 text-white hover:bg-white/25 hover:text-white backdrop-blur-sm"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    <span>Notifica</span>
                  </Button>
                  {series && (
                    <div className="w-full rounded-md bg-white/10 p-2 text-xs">
                      <p>
                        🔁 Si ripete: la partita del {formatSeriesDate(series.next_date_time)} esce {formatSeriesDate(series.publish_at)}
                        {series.remaining == null ? '' : series.remaining > 1 ? ` (ancora ${series.remaining})` : ' (ultima)'}.
                      </p>
                      <button
                        type="button"
                        onClick={handleStopSeries}
                        className="mt-1 underline underline-offset-2 opacity-90 hover:opacity-100"
                      >
                        Interrompi ripetizione
                      </button>
                      {match.league_id && (
                        <Link
                          to={`/leagues/${match.league_id}?tab=series`}
                          className="mt-1 ml-3 underline underline-offset-2 opacity-90 hover:opacity-100"
                        >
                          Modifica
                        </Link>
                      )}
                    </div>
                  )}
                  </div>
                </details>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <Countdown dateTime={match.date_time} />
                {!mvp?.has_started && <ParticipantsAvatarStack participants={confirmedParticipants} max={4} />}
                {!isArchived && !mvp?.has_started && <RichJoinButton
                  match={match}
                  userParticipation={userParticipation}
                  confirmedParticipants={confirmedParticipants}
                  reserveParticipants={reserveParticipants}
                  isFull={isFull}
                  reservesFull={reservesFull}
                  onJoin={() => setShowJoinDialog(true)}
                  onLeave={handleLeave}
                  currentUserId={user?.user_id}
                />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <nav
        aria-label="Sezioni della partita"
        className="safe-content-sticky sticky z-20 -mx-4 -mt-6 mb-4 flex gap-2 overflow-x-auto border-b bg-background/95 px-4 py-2 backdrop-blur md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {MATCH_SECTIONS.map(([sectionId, label]) => (
          <button
            key={sectionId}
            type="button"
            onClick={() => jumpToSection(sectionId)}
            className="shrink-0 rounded-full border px-3 py-1 text-sm font-medium hover:bg-muted"
          >
            {label}
          </button>
        ))}
      </nav>
      <div id="mvp" className="mb-6 scroll-mt-28"><MatchMvp key={id} matchId={id} mvp={mvp} participants={participants} onVote={setMvp} viewerId={user?.user_id} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Date & Location */}
          <Card id="info" className="scroll-mt-28">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Data</p>
                  <p className="text-muted-foreground">
                    {formatDate(match.date_time, { weekday: 'long' })} alle {formatTime(match.date_time)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-6 w-6 text-accent" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-lg">{match.location_name}</p>
                  {match.location_address && (
                    <p className="text-muted-foreground">{match.location_address}</p>
                  )}
                  {(match.location_address || match.location_name || match.location_map_url) && (
                    <a
                      href={
                        match.location_address || match.location_name
                          ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(match.location_address || match.location_name)}`
                          : match.location_map_url
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Apri in Google Maps <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {match.facility_id && (
                    <Link
                      to={`/facilities/${match.facility_id}`}
                      className="inline-flex items-center gap-1 mt-2 text-sm text-primary hover:underline"
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      Vedi struttura
                    </Link>
                  )}
                </div>
              </div>

              {match.notes && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">{match.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {(match.payment_amount != null || match.payment_link || match.payment_notes) && (
            <Card data-testid="match-payment-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <Euro className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="font-semibold text-lg">Info pagamento</p>
                      {match.payment_amount != null && (
                        <span className="text-2xl font-bold text-emerald-600">
                          €{Number(match.payment_amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {match.payment_notes && (
                      <p className="text-sm text-muted-foreground">{match.payment_notes}</p>
                    )}
                    {match.payment_link && !isArchived && (
                      <a
                        href={match.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-primary hover:underline break-all"
                        data-testid="match-payment-link"
                      >
                        Paga con link <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!isArchived && (
            <MatchPaymentsSection
              match={match}
              participants={participants}
              currentUser={user}
            />
          )}

          {(teamAInfo || teamBInfo) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Rosa Squadre
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {teamAInfo && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Badge variant="outline">Team A</Badge>
                      <span className="text-muted-foreground text-sm">{teamAInfo.name}</span>
                      <Badge variant="secondary" className="text-xs">{teamAInfo.skill_level_sum ?? 0} pt skill</Badge>
                    </h4>
                    {teamAInfo.members_info?.length ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {teamAInfo.members_info.map(m => (
                          <div key={m.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded p-1 transition-colors" onClick={() => setProfileUserId(m.user_id)}>
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={m.picture} />
                              <AvatarFallback className="text-xs">{getInitials(m.name)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <span className="text-sm block truncate">{m.name}</span>
                              {m.nickname && <span className="text-[10px] text-primary italic">&laquo;{m.nickname}&raquo;</span>}
                            </div>
                            <span className="text-xs text-muted-foreground ml-auto">L{m.skill_level ?? 3}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nessun membro registrato</p>
                    )}
                  </div>
                )}

                {teamBInfo && (
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Badge variant="outline">Team B</Badge>
                      <span className="text-muted-foreground text-sm">{teamBInfo.name}</span>
                      <Badge variant="secondary" className="text-xs">{teamBInfo.skill_level_sum ?? 0} pt skill</Badge>
                    </h4>
                    {teamBInfo.members_info?.length ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {teamBInfo.members_info.map(m => (
                          <div key={m.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded p-1 transition-colors" onClick={() => setProfileUserId(m.user_id)}>
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={m.picture} />
                              <AvatarFallback className="text-xs">{getInitials(m.name)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <span className="text-sm block truncate">{m.name}</span>
                              {m.nickname && <span className="text-[10px] text-primary italic">&laquo;{m.nickname}&raquo;</span>}
                            </div>
                            <span className="text-xs text-muted-foreground ml-auto">L{m.skill_level ?? 3}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nessun membro registrato</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {canManageRoster && (
            <details className="rounded-xl border p-4">
              <summary className="cursor-pointer font-medium">Gestisci giocatori e squadre</summary>
              <div className="mt-4">
            <RosterBoard
              participants={participants}
              isTeamLocked={isTeamLocked}
              maxParticipants={match?.max_participants || 10}
              maxReserves={maxReserves}
              draggingUserId={draggingUserId}
              onDragStart={setDraggingUserId}
              onDragEnd={() => setDraggingUserId(null)}
              onMoveToZone={handleMoveToZone}
              onRemove={handleRemoveParticipant}
              onAddClick={handleOpenAddPlayerDialog}
              onAvatarClick={(uid) => setProfileUserId(uid)}
            />
              </div>
            </details>
          )}

          <details id="campo" className="scroll-mt-28 rounded-xl border p-4">
            <summary className="cursor-pointer font-medium">Disposizione in campo</summary>
            <div className="mt-4"><FieldPreview teamA={teamA} teamB={teamB} onPlayerClick={setProfileUserId} /></div>
          </details>

          {match?.goalkeeper_rotation_enabled && (teamA.length > 0 || teamB.length > 0) && (
            <GoalkeeperRotationPanel teamA={teamA} teamB={teamB} />
          )}

          {/* Participants */}
          <Card id="squadre" className="scroll-mt-28">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Titolari
                  <span className="text-sm font-normal text-muted-foreground tabular-nums">
                    {confirmedParticipants.length}/{match.max_participants}
                  </span>
                </span>
                {isFull && (
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white border-0">Completo</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {confirmedParticipants.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Nessun partecipante ancora</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {teamA.length > 0 && (
                    <div className="rounded-xl bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border border-blue-500/20 p-3">
                      <h4 className="font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-widest text-blue-700 dark:text-blue-300">
                        <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                        Team A
                        <span className="text-muted-foreground font-normal tabular-nums">({teamA.length})</span>
                        <Badge variant="secondary" className="ml-auto text-[10px] normal-case tracking-normal">
                          {teamASkillTotal} pt skill
                        </Badge>
                      </h4>
                      <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6">
                        {teamA.map(p => (
                          <ParticipantCard key={p.participant_id} participant={p} accent="blue" onClick={p.is_guest ? undefined : () => setProfileUserId(p.user_id)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {teamB.length > 0 && (
                    <div className="rounded-xl bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent border border-rose-500/20 p-3">
                      <h4 className="font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-widest text-rose-700 dark:text-rose-300">
                        <span className="inline-block h-2 w-2 rounded-full bg-rose-500" />
                        Team B
                        <span className="text-muted-foreground font-normal tabular-nums">({teamB.length})</span>
                        <Badge variant="secondary" className="ml-auto text-[10px] normal-case tracking-normal">
                          {teamBSkillTotal} pt skill
                        </Badge>
                      </h4>
                      <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6">
                        {teamB.map(p => (
                          <ParticipantCard key={p.participant_id} participant={p} accent="rose" onClick={p.is_guest ? undefined : () => setProfileUserId(p.user_id)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {noTeam.length > 0 && (
                    <div className="rounded-xl bg-muted/40 border border-muted p-3">
                      <h4 className="font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
                        <span className="inline-block h-2 w-2 rounded-full bg-slate-500" />
                        Senza squadra
                        <span className="font-normal tabular-nums">({noTeam.length})</span>
                      </h4>
                      <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-5 md:grid-cols-6">
                        {noTeam.map(p => (
                          <ParticipantCard key={p.participant_id} participant={p} accent="slate" onClick={p.is_guest ? undefined : () => setProfileUserId(p.user_id)} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reserves */}
          <Card id="riserve" className="scroll-mt-28">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  Riserve
                  <span className="text-sm font-normal text-muted-foreground tabular-nums">
                    {reserveParticipants.length}/{maxReserves}
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reserveParticipants.length === 0 ? (
                <div className="text-center text-muted-foreground py-6 text-sm">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  Nessuna riserva in lista
                </div>
              ) : (
                <div className="space-y-2">
                  {reserveParticipants
                    .sort((a, b) => new Date(a.joined_at) - new Date(b.joined_at))
                    .map((p, index) => (
                      <button
                        type="button"
                        key={p.participant_id}
                        onClick={p.is_guest ? undefined : () => setProfileUserId(p.user_id)}
                        disabled={p.is_guest}
                        className="w-full flex items-center gap-3 p-2.5 rounded-lg border bg-gradient-to-r from-amber-500/5 to-transparent hover:from-amber-500/10 hover:border-amber-500/40 transition-all text-left disabled:cursor-default"
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold tabular-nums ${
                          index === 0
                            ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md'
                            : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                        }`}>
                          {index + 1}
                        </div>
                        <Avatar className="h-9 w-9 ring-2 ring-amber-500/30">
                          <AvatarImage src={p.user?.picture} />
                          <AvatarFallback className="text-xs">{getInitials(p.user?.name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p.user?.name}</p>
                          {p.user?.nickname && (
                            <p className="text-[11px] italic text-primary truncate">«{p.user.nickname}»</p>
                          )}
                        </div>
                        {p.is_guest && (
                          <Badge variant="outline" className="text-[10px]">Ospite</Badge>
                        )}
                        {index === 0 && (
                          <Badge className="bg-amber-500 hover:bg-amber-500 text-white border-0 text-[11px] uppercase tracking-widest">
                            Prossimo
                          </Badge>
                        )}
                      </button>
                    ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Le riserve subentrano automaticamente se un titolare lascia la partita
              </p>
            </CardContent>
          </Card>

          {/* Result (if completed) */}
          <details id="eventi" className="scroll-mt-28 rounded-xl border p-4">
            <summary className="cursor-pointer font-medium">Eventi e foto della partita</summary>
            <div className="mt-4 space-y-4">
          {/* Match Events (Goals, Cards, etc.) */}
          <MatchEventsSection 
            matchId={id}
            participants={participants}
            isOrganizer={isOrganizer}
            teamA={teamA}
            teamB={teamB}
          />

          {/* Match Media Gallery */}
          <MatchMediaGallery 
            matchId={id}
            isOrganizer={isOrganizer}
            isParticipant={!isArchived && !!userParticipation}
          />
            </div>
          </details>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <Card>
            <CardHeader>
              <CardTitle>Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Sport</span>
                <Badge variant="secondary">{sport?.name}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Titolari</span>
                <span className={`font-medium ${isFull ? 'text-green-600' : ''}`}>
                  {confirmedParticipants.length}/{match.max_participants}
                  {isFull && ' ✓'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Riserve</span>
                <span className="font-medium text-orange-600">
                  {reserveParticipants.length}/{maxReserves}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Visibilità</span>
                <Badge variant="outline">
                  {match.is_public ? 'Pubblica' : 'Privata'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Creata</span>
                <span className="text-sm">{formatDate(match.created_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Share */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  navigator.clipboard.writeText(appUrl(`/matches/${id}`)).then(() => toast.success('Link copiato!')).catch(() => toast.error('Impossibile copiare il link'));
                }}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Copia Link
              </Button>
              <Button 
                variant="outline" 
                className="w-full bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-950/40 dark:hover:bg-green-900/50 dark:text-green-300 dark:border-green-800"
                onClick={handleShareWhatsApp}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Condividi su WhatsApp
              </Button>
            </CardContent>
          </Card>

        </div>
      </div>
      </div>

      {/* Join Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Partecipa alla partita</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {isTeamLocked ? (
              <p className="text-muted-foreground">
                La squadra viene assegnata automaticamente in base alla rosa della competizione.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Verrai aggiunto come {isFull ? 'riserva' : 'titolare'}. Le squadre vengono formate dagli admin e dai gestori della lega.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleJoin} disabled={joining} data-testid="confirm-join-match-btn">
              {joining ? 'Iscrizione...' : 'Partecipa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Goal className="h-5 w-5" />
              Inserisci Risultato
            </DialogTitle>
            <DialogDescription>
              Inserisci il punteggio finale della partita
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-center gap-8 py-6">
            <div className="text-center space-y-2">
              <Label className="text-muted-foreground">Team A</Label>
              <Input
                type="number"
                min="0"
                max="99"
                value={scoreA}
                onChange={(e) => setScoreA(e.target.value)}
                className="w-20 text-center text-2xl font-bold h-14"
                placeholder="0"
                data-testid="score-a-input"
              />
              <p className="text-xs text-muted-foreground">
                {teamA.map(p => p.user?.name).filter(Boolean).join(', ')}
              </p>
            </div>
            <span className="text-3xl text-muted-foreground">-</span>
            <div className="text-center space-y-2">
              <Label className="text-muted-foreground">Team B</Label>
              <Input
                type="number"
                min="0"
                max="99"
                value={scoreB}
                onChange={(e) => setScoreB(e.target.value)}
                className="w-20 text-center text-2xl font-bold h-14"
                placeholder="0"
                data-testid="score-b-input"
              />
              <p className="text-xs text-muted-foreground">
                {teamB.map(p => p.user?.name).filter(Boolean).join(', ')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveResult} disabled={savingResult} data-testid="save-result-btn">
              {savingResult ? 'Salvataggio...' : 'Salva Risultato'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Invia una notifica
            </DialogTitle>
            <DialogDescription>
              Scegli chi avvisare per questa partita
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-start gap-3">
                <Send className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Avvisa la lega</p>
                  <p className="text-sm text-muted-foreground">
                    Tutti i membri della lega ricevono “Nuova partita” con data, ora, luogo e il link per iscriversi.
                  </p>
                </div>
              </div>
              <Button
                className="w-full"
                onClick={handleAnnounceMatch}
                disabled={sendingNotifications}
                data-testid="send-notify-btn"
              >
                {sendingNotifications ? 'Invio...' : 'Avvisa la lega'}
              </Button>
            </div>

            <div className="p-4 rounded-lg border space-y-3">
              <div className="flex items-start gap-3">
                <Bell className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Ricorda ai convocati</p>
                  <p className="text-sm text-muted-foreground">
                    Solo i titolari confermati ({confirmedParticipants.length}) ricevono un promemoria con data, ora e luogo.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleRemindParticipants}
                disabled={sendingNotifications}
                data-testid="remind-participants-btn"
              >
                {sendingNotifications ? 'Invio...' : 'Ricorda ai convocati'}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNotifyDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggest Teams Dialog */}
      <Dialog open={showSuggestDialog} onOpenChange={setShowSuggestDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shuffle className="h-5 w-5" />
              Squadre bilanciate
            </DialogTitle>
            <DialogDescription>
              {suggestion
                ? `${suggestion.team_size} vs ${suggestion.team_size} — punteggio bilanciamento ${(suggestion.balance_score ?? 0).toFixed(1)} (${suggestion.method})`
                : 'Generazione in corso...'}
            </DialogDescription>
          </DialogHeader>

          {suggestLoading && (
            <p className="text-center py-6 text-muted-foreground">Generazione squadre...</p>
          )}

          {suggestion && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              {[
                { label: 'Squadra A', list: suggestion.team_a, skill: suggestion.team_a_skill_sum, color: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/40' },
                { label: 'Squadra B', list: suggestion.team_b, skill: suggestion.team_b_skill_sum, color: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/40' },
              ].map((t) => (
                <div key={t.label} className={`rounded-lg border-2 ${t.color} p-3`}>
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-semibold text-sm">{t.label}</p>
                    <Badge variant="secondary">{t.skill} pt skill</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-3">
                    {t.list.map((p) => (
                      <FifaMiniCard key={p.user_id} player={p} attributes={p} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {suggestion?.unused?.length > 0 && (
            <div className="rounded-lg border bg-muted/50 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-1">
                Non utilizzati ({suggestion.unused.length})
              </p>
              <p className="text-xs text-muted-foreground">
                {suggestion.unused.map((p) => p.name).join(', ')}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggestDialog(false)}>
              Annulla
            </Button>
            <Button variant="outline" onClick={handleSuggestTeams} disabled={suggestLoading}>
              <Shuffle className="h-4 w-4 mr-2" />
              Rigenera
            </Button>
            <Button onClick={handleApplySuggestion} disabled={!suggestion || applyingSuggestion}>
              {applyingSuggestion ? 'Applicazione...' : 'Applica squadre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Player Profile Dialog */}
      <PlayerProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onOpenChange={(open) => !open && setProfileUserId(null)}
      />

      {/* Add Player Dialog */}
      {canManageRoster && (
        <AddPlayerDialog
          open={showAddPlayerDialog}
          onOpenChange={setShowAddPlayerDialog}
          candidates={availableRosterUsers}
          loading={loadingRoster}
          isTeamLocked={isTeamLocked}
          search={rosterSearch}
          onSearchChange={setRosterSearch}
          selectedUser={selectedRosterUser}
          onSelectUser={setSelectedRosterUser}
          selectedTeam={selectedRosterTeam}
          onSelectTeam={setSelectedRosterTeam}
          selectedStatus={selectedRosterStatus}
          onSelectStatus={setSelectedRosterStatus}
          submitting={addingParticipant}
          onSubmit={() => handleAddParticipant()}
          onSubmitGuest={handleAddParticipant}
        />
      )}
    </div>
  );
};

const ZONE_STYLES = {
  A: {
    label: 'Team A',
    accent: 'from-blue-500 to-indigo-600',
    soft: 'from-blue-500/10 via-blue-500/5',
    ring: 'ring-blue-500/40',
    border: 'border-blue-500/30',
    activeBg: 'bg-blue-500/15',
    chip: 'bg-blue-500/15 text-blue-600 dark:text-blue-300',
  },
  none: {
    label: 'Senza squadra',
    accent: 'from-slate-400 to-slate-500',
    soft: 'from-slate-500/10 via-slate-500/5',
    ring: 'ring-slate-400/40',
    border: 'border-slate-400/30',
    activeBg: 'bg-slate-400/15',
    chip: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
  },
  B: {
    label: 'Team B',
    accent: 'from-rose-500 to-red-600',
    soft: 'from-rose-500/10 via-rose-500/5',
    ring: 'ring-rose-500/40',
    border: 'border-rose-500/30',
    activeBg: 'bg-rose-500/15',
    chip: 'bg-rose-500/15 text-rose-600 dark:text-rose-300',
  },
  reserve: {
    label: 'Riserve',
    accent: 'from-amber-500 to-orange-600',
    soft: 'from-amber-500/10 via-amber-500/5',
    ring: 'ring-amber-500/40',
    border: 'border-amber-500/30',
    activeBg: 'bg-amber-500/15',
    chip: 'bg-amber-500/15 text-amber-600 dark:text-amber-300',
  },
};

const RosterChip = ({ participant, zone, isDragging, isLocked, onDragStart, onDragEnd, onAvatarClick, onRemove, onQuickMove }) => {
  const style = ZONE_STYLES[zone];
  const isGuest = !!participant.is_guest;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', participant.user_id);
        onDragStart(participant.user_id);
      }}
      onDragEnd={onDragEnd}
      className={`group relative flex items-center gap-2 p-2 rounded-lg bg-card border cursor-grab active:cursor-grabbing transition-all hover:shadow-md ${
        isDragging ? 'opacity-50 scale-95' : ''
      }`}
      data-testid={`roster-chip-${participant.user_id}`}
    >
      <button
        type="button"
        onClick={isGuest ? undefined : () => onAvatarClick(participant.user_id)}
        disabled={isGuest}
        className="shrink-0 rounded-full disabled:cursor-default"
        aria-label={isGuest ? `${participant.user?.name || 'Giocatore'} è un ospite` : `Apri il profilo di ${participant.user?.name || 'giocatore'}`}
      >
        <Avatar className={`h-8 w-8 ring-2 ${style.ring}`}>
          <AvatarImage src={participant.user?.picture} />
          <AvatarFallback className="text-xs">{getInitials(participant.user?.name)}</AvatarFallback>
        </Avatar>
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate leading-tight">
          {participant.user?.name || 'Utente'}
        </p>
        {participant.user?.nickname && (
          <p className="text-[10px] italic text-primary truncate">«{participant.user.nickname}»</p>
        )}
        {isGuest && (
          <Badge variant="outline" className="mt-0.5 px-1 py-0 text-[9px]">Ospite</Badge>
        )}
      </div>
      {/* Mobile quick-move */}
      <div className="md:hidden flex gap-0.5 shrink-0">
        {zone !== 'A' && !isLocked && (
          <button onClick={() => onQuickMove(participant.user_id, 'A')} className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/15 text-blue-600 dark:text-blue-300 font-semibold">A</button>
        )}
        {zone !== 'B' && !isLocked && (
          <button onClick={() => onQuickMove(participant.user_id, 'B')} className="px-1.5 py-0.5 rounded text-[10px] bg-rose-500/15 text-rose-600 dark:text-rose-300 font-semibold">B</button>
        )}
        {zone !== 'reserve' && (
          <button onClick={() => onQuickMove(participant.user_id, 'reserve')} className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-300 font-semibold">R</button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(participant.user_id)}
        className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 rounded flex items-center justify-center hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
        aria-label="Rimuovi"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const RosterColumn = ({ zone, items, count, max, isLocked, draggingUserId, onDragStart, onDragEnd, onMoveToZone, onAvatarClick, onRemove }) => {
  const style = ZONE_STYLES[zone];
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isOver) setIsOver(true);
  };
  const handleDragLeave = () => setIsOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    const userId = e.dataTransfer.getData('text/plain');
    if (userId) onMoveToZone(userId, zone);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative rounded-xl border-2 transition-all ${
        isOver ? `${style.border} ${style.activeBg} ring-2 ${style.ring}` : 'border-dashed border-muted'
      }`}
    >
      {/* Header strip */}
      <div className={`flex items-center justify-between px-3 py-2 rounded-t-[10px] bg-gradient-to-r ${style.accent} text-white text-xs font-semibold uppercase tracking-widest`}>
        <span>{style.label}</span>
        <span className="tabular-nums bg-white/20 rounded-full px-2 text-[11px]">
          {count}{max ? `/${max}` : ''}
        </span>
      </div>

      <div className="p-2 space-y-1.5 min-h-[80px]">
        {items.length === 0 ? (
          <p className="text-[11px] text-muted-foreground text-center py-3">
            {isOver ? 'Rilascia qui' : 'Trascina qui'}
          </p>
        ) : (
          items.map(p => (
            <RosterChip
              key={p.participant_id}
              participant={p}
              zone={zone}
              isDragging={draggingUserId === p.user_id}
              isLocked={isLocked}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onAvatarClick={onAvatarClick}
              onRemove={onRemove}
              onQuickMove={onMoveToZone}
            />
          ))
        )}
      </div>
    </div>
  );
};

const RosterBoard = ({
  participants,
  isTeamLocked,
  maxParticipants,
  maxReserves,
  draggingUserId,
  onDragStart,
  onDragEnd,
  onMoveToZone,
  onRemove,
  onAddClick,
  onAvatarClick,
}) => {
  const confirmed = participants.filter(p => p.status === 'confirmed');
  const reserves = participants.filter(p => p.status === 'reserve');
  const teamA = confirmed.filter(p => p.team === 'A');
  const teamB = confirmed.filter(p => p.team === 'B');
  const noTeam = confirmed.filter(p => !p.team);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5 text-primary" />
          Lavagna tattica
          <span className="text-xs font-normal text-muted-foreground">
            ({participants.length} {participants.length === 1 ? 'giocatore' : 'giocatori'})
          </span>
        </CardTitle>
        <Button size="sm" onClick={onAddClick}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          Aggiungi
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground hidden md:block">
          Trascina i giocatori tra le colonne per cambiare squadra o spostarli in riserva. Su mobile usa i tasti A/B/R.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <RosterColumn
            zone="A"
            items={teamA}
            count={teamA.length}
            isLocked={isTeamLocked}
            draggingUserId={draggingUserId}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onMoveToZone={onMoveToZone}
            onAvatarClick={onAvatarClick}
            onRemove={onRemove}
          />
          <RosterColumn
            zone="none"
            items={noTeam}
            count={noTeam.length}
            isLocked={isTeamLocked}
            draggingUserId={draggingUserId}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onMoveToZone={onMoveToZone}
            onAvatarClick={onAvatarClick}
            onRemove={onRemove}
          />
          <RosterColumn
            zone="B"
            items={teamB}
            count={teamB.length}
            isLocked={isTeamLocked}
            draggingUserId={draggingUserId}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onMoveToZone={onMoveToZone}
            onAvatarClick={onAvatarClick}
            onRemove={onRemove}
          />
        </div>
        <RosterColumn
          zone="reserve"
          items={reserves}
          count={reserves.length}
          max={maxReserves}
          isLocked={isTeamLocked}
          draggingUserId={draggingUserId}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onMoveToZone={onMoveToZone}
          onAvatarClick={onAvatarClick}
          onRemove={onRemove}
        />
        {/* Capacity bar */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            Titolari: <strong className="text-foreground tabular-nums">{confirmed.length}/{maxParticipants}</strong>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
            Riserve: <strong className="text-foreground tabular-nums">{reserves.length}/{maxReserves}</strong>
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const AddPlayerDialog = ({
  open,
  onOpenChange,
  candidates,
  loading,
  isTeamLocked,
  search,
  onSearchChange,
  selectedUser,
  onSelectUser,
  selectedTeam,
  onSelectTeam,
  selectedStatus,
  onSelectStatus,
  onSubmit,
  onSubmitGuest,
  submitting,
}) => {
  const filtered = search.trim()
    ? candidates.filter(u => {
        const q = search.trim().toLowerCase();
        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
      })
    : candidates;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Aggiungi giocatore
          </DialogTitle>
          <DialogDescription>
            Aggiungi un membro della lega o un ospite solo per questa partita.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="roster-player-search">Nome o email</Label>
            <Input
              id="roster-player-search"
              autoFocus
              autoComplete="off"
              maxLength={100}
              placeholder="Cerca per nome o email..."
              value={search}
              onChange={(event) => {
                onSearchChange(event.target.value);
                onSelectUser('');
              }}
            />
          </div>
          <div className="max-h-64 overflow-y-auto border rounded-lg divide-y" aria-live="polite">
            {loading ? (
              <p className="p-4 text-sm text-muted-foreground text-center">Caricamento...</p>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground text-center">
                {candidates.length === 0 ? 'Nessun membro disponibile' : 'Nessun risultato'}
              </p>
            ) : (
              filtered.map(u => {
                const status = u.member_status;
                const isApproved = !status || status === 'approved';
                const isSelected = selectedUser === u.user_id;
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => isApproved && onSelectUser(u.user_id)}
                    disabled={!isApproved}
                    className={`w-full flex items-center gap-3 p-2.5 text-left transition-colors ${
                      isSelected ? 'bg-primary/10' : 'hover:bg-muted/60'
                    } ${!isApproved ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={u.picture} />
                      <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.name || u.email || 'Utente'}</p>
                      {status && status !== 'approved' && (
                        <p className="text-[11px] text-muted-foreground">In attesa</p>
                      )}
                    </div>
                    {isSelected && (
                      <Badge variant="default" className="text-[10px]">Selezionato</Badge>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="roster-team" className="text-xs uppercase tracking-widest">Squadra</Label>
              <Select value={selectedTeam} onValueChange={onSelectTeam} disabled={isTeamLocked}>
                <SelectTrigger id="roster-team">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">Team A</SelectItem>
                  <SelectItem value="B">Team B</SelectItem>
                  <SelectItem value="none">Senza squadra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="roster-status" className="text-xs uppercase tracking-widest">Stato</Label>
              <Select value={selectedStatus} onValueChange={onSelectStatus}>
                <SelectTrigger id="roster-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Titolare</SelectItem>
                  <SelectItem value="reserve">Riserva</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {search.trim() && (
            <div className="space-y-1.5">
              <Button
                type="button"
                variant="outline"
                className="min-h-11 h-auto w-full whitespace-normal"
                onClick={() => onSubmitGuest(search)}
                disabled={submitting || isTeamLocked}
                aria-describedby="guest-player-help"
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                Aggiungi &ldquo;{search.trim()}&rdquo; come ospite
              </Button>
              <p id="guest-player-help" className="text-xs text-muted-foreground">
                {isTeamLocked
                  ? 'Gli ospiti non sono disponibili nelle partite con squadre già fissate.'
                  : 'Vale solo per questa partita: non verrà aggiunto ai membri della lega.'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={submitting || !selectedUser}
            >
              {submitting ? 'Aggiungo...' : 'Aggiungi alla rosa'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// From each team's goal to the halfway line: defenders, then the balanced, then attackers.
const ROLE_ROWS = ['defensive', 'balanced', 'offensive'];
const rowsByRole = (players) => ROLE_ROWS
  .map((role) => players.filter((p) => (p.attributes?.play_style || 'balanced') === role))
  .filter((row) => row.length);

const FieldPreview = ({ teamA, teamB, onPlayerClick }) => {
  const renderTeam = (players, { label, bar }, attackDown) => {
    if (!players.length) {
      return <p className="py-6 text-center text-xs text-white/60">{label}: nessun giocatore</p>;
    }
    const rows = rowsByRole(players);
    const widest = Math.max(...rows.map((row) => row.length));
    const width = widest <= 3 ? 'w-[26%]' : widest === 4 ? 'w-[22%]' : 'w-[18%]';
    return (
      <div className="space-y-3">
        {(attackDown ? rows : [...rows].reverse()).map((row) => (
          <div key={row.map((p) => p.participant_id).join()} className="flex flex-wrap justify-center gap-2">
            {row.map((p) => (
              <div key={p.participant_id} className={`${width} max-w-[5.5rem]`}>
                <FifaMiniCard
                  player={p.user}
                  attributes={p.attributes}
                  onClick={p.is_guest ? undefined : () => onPlayerClick?.(p.user_id)}
                />
                <div className={`mx-auto mt-1 h-1 w-1/2 rounded-full ${bar}`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5" />
          Anteprima Campo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="relative overflow-hidden rounded-xl border border-emerald-900/40 px-2 py-4"
          style={{ background: 'repeating-linear-gradient(180deg, #1f7a43 0 44px, #237f48 44px 88px)' }}
        >
          {/* Pitch lines */}
          <div className="pointer-events-none absolute inset-2 rounded-lg border-2 border-white/25" />
          <div className="pointer-events-none absolute inset-x-2 top-1/2 h-0.5 -translate-y-1/2 bg-white/25" />
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/25" />
          <div className="pointer-events-none absolute left-1/2 top-2 h-12 w-2/5 -translate-x-1/2 rounded-b-lg border-2 border-t-0 border-white/25" />
          <div className="pointer-events-none absolute bottom-2 left-1/2 h-12 w-2/5 -translate-x-1/2 rounded-t-lg border-2 border-b-0 border-white/25" />

          <div className="relative flex flex-col gap-8">
            <div className="flex items-center gap-1.5 px-2 text-xs font-semibold text-white"><span className="h-2 w-2 rounded-full bg-blue-400" />Squadra A</div>
            {renderTeam(teamA, { label: 'Squadra A', bar: 'bg-blue-400' }, true)}
            {renderTeam(teamB, { label: 'Squadra B', bar: 'bg-rose-400' }, false)}
            <div className="flex items-center gap-1.5 px-2 text-xs font-semibold text-white"><span className="h-2 w-2 rounded-full bg-rose-400" />Squadra B</div>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Difensori vicino alla porta, attaccanti verso il centro. Il colore della card segue la forma.</p>
      </CardContent>
    </Card>
  );
};

const PARTICIPANT_ACCENTS = {
  blue: 'bg-blue-500',
  rose: 'bg-rose-500',
  slate: 'bg-slate-400',
};

const ParticipantCard = ({ participant, accent = 'slate', onClick }) => (
  <div>
    <FifaMiniCard
      player={participant.user}
      attributes={participant.attributes}
      onClick={participant.is_guest ? undefined : onClick}
      note={participant.is_guest ? 'Ospite' : undefined}
    />
    <div className={`mx-auto mt-1 h-1 w-1/2 rounded-full ${PARTICIPANT_ACCENTS[accent] || PARTICIPANT_ACCENTS.slate}`} />
  </div>
);

export default MatchDetailPage;
